import { FC } from 'react';

import { useTranslation } from 'react-i18next';

import { Box, Card, Icon, Spinner, Text } from '@nimbus-ds/components';
import {
  CashIcon,
  ChevronRightIcon,
  RedoIcon,
  TiendanubeIcon
} from '@nimbus-ds/icons';

import Domain from 'commons/utils/domains';

import { EAddCredit } from 'domains/balance/types/add-credit';

interface PaymentMethodProps {
  type: EAddCredit;
  disabled?: boolean;
  onClick?: () => void;
  title: string;
  subtitle?: string;
  balance?: string;
  loading?: boolean;
  unavailable?: boolean;
  isAvailableBalance?: boolean;
  canRetryBalance?: boolean;
  balanceWithoutCurrency?: number;
}

const PaymentMethod: FC<PaymentMethodProps> = ({
  type,
  disabled = false,
  onClick,
  title,
  subtitle,
  balance,
  loading = false,
  unavailable = false,
  isAvailableBalance,
  canRetryBalance,
  balanceWithoutCurrency
}) => {
  const { t } = useTranslation(Domain.BALANCE);

  const shouldDisable = disabled || unavailable || isAvailableBalance === false;

  const handleClick = shouldDisable ? undefined : onClick;

  const renderIcon = () => {
    if (type === EAddCredit.NUVEMPAGO)
      return <Icon source={<TiendanubeIcon />} />;
    if (loading) return <Spinner size="small" />;

    return <Icon source={<CashIcon />} />;
  };

  const renderSubtitle = () => {
    if (type !== EAddCredit.NUVEMPAGO) return null;

    if (balanceWithoutCurrency === 0) {
      return (
        <Text color="neutral-textDisabled">
          {t('labels.unavailable-balance')} {balance}
        </Text>
      );
    }

    if (canRetryBalance) {
      return (
        <Text color="danger-interactive">{t('errors.loaded-balance')}</Text>
      );
    }

    if (unavailable) {
      return (
        <Text color="neutral-textDisabled">
          {t('labels.unavailable-service')}
        </Text>
      );
    }

    if (isAvailableBalance) {
      return (
        <Text color="success-interactive">
          {t('labels.available-balance')} {balance}
        </Text>
      );
    }

    return (
      <Text color="neutral-textDisabled">
        {t('labels.unavailable-balance')} {balance}
      </Text>
    );
  };

  return (
    <Box
      cursor={shouldDisable ? 'not-allowed' : 'pointer'}
      onClick={handleClick}
      id={`payment-method-${type}`}
    >
      <Card
        backgroundColor={
          shouldDisable ? 'neutral-surface' : 'neutral-background'
        }
      >
        <Card.Body>
          <Box
            width="100%"
            display="flex"
            gap="2"
            alignItems={type === EAddCredit.NUVEMPAGO ? 'flex-start' : 'center'}
          >
            {renderIcon()}

            <Box>
              <Text>
                <Text as="span" fontWeight="bold">
                  {title}
                </Text>{' '}
                {subtitle}
              </Text>
              {renderSubtitle()}
            </Box>

            <Box ml="auto" my="auto">
              {canRetryBalance && balanceWithoutCurrency !== 0 ? (
                <Icon source={<RedoIcon />} />
              ) : (
                <Icon source={<ChevronRightIcon />} />
              )}
            </Box>
          </Box>
        </Card.Body>
      </Card>
    </Box>
  );
};

export default PaymentMethod;
